import { Link } from "react-router";
import { LuPencil, LuPlay } from "react-icons/lu";
import ListTableRegion from "../../common/ListTableRegion";
import {
    formatCustomSiteCellValue,
    formatCustomSiteResultsFoundLabel,
    getPreEnrollmentRowActionLabel,
    getPreEnrollmentStatusTone,
    isPreEnrollmentCustomizationStarted,
} from "../../../../utils/planManager/preEnrollmentCustomUtils.js";
import "./MainContent.scss";

/**
 * Sortable table column metadata, in display order. Edit is not sortable.
 * @readonly
 * @type {ReadonlyArray<{ field: string | null, label: string }>}
 */
export const CUSTOM_SITE_TABLE_COLUMNS = Object.freeze([
    { field: "clientName", label: "Client Name" },
    { field: "oeUrlName", label: "Pre-Enrollment URL name" },
    { field: "status", label: "Status" },
    { field: null, label: "Edit" },
]);


/**
 * Drill-down route path reached from Start / Edit on the custom-sites list.
 * @readonly
 * @type {{ SITE_CUSTOMIZATION: string }}
 */
export const PRE_ENROLLMENT_CUSTOM_ROUTE_PATHS = Object.freeze({
    SITE_CUSTOMIZATION: "/plan-manager/pre-enrollment-custom/custom",
});

const CUSTOM_SITE_TABLE_COLUMN_DEFS = CUSTOM_SITE_TABLE_COLUMNS.map(
    (column) => ({
        key: column.label,
        label: column.label,
        sortable: Boolean(column.field),
        field: column.field,
    }),
);

/**
 * Pre-enrollment custom list table and optional search-result count. Pure
 * presentational — emits user intent upward via callbacks; owns no state.
 *
 * @param {object} props
 * @param {Array<Object>} props.customSites - Filtered and sorted rows to display.
 * @param {string} props.searchCriteria - Current search text; count is shown when non-empty.
 * @param {string | null} props.sortedField - Active sort column field, or null when unsorted.
 * @param {boolean} props.isSortDescending - Whether the active sort is descending.
 * @param {(field: string) => void} props.onSort - Cycles sort for a sortable column field.
 * @returns {import('react').JSX.Element}
 */
const MainContent = ({
    customSites,
    searchCriteria,
    sortedField,
    isSortDescending,
    onSort,
}) => {
    const visibleRows = customSites ?? [];
    const isSearchActive = Boolean(searchCriteria);
    const isEmpty = visibleRows.length === 0;
    const tableClassName = isEmpty
        ? "pre-enrollment-custom-table is-empty"
        : "pre-enrollment-custom-table";

    return (
        <ListTableRegion
            resultsLabel={
                isSearchActive
                    ? formatCustomSiteResultsFoundLabel(visibleRows.length)
                    : undefined
            }
            tableClassName={tableClassName}
            columnCount={CUSTOM_SITE_TABLE_COLUMNS.length}
            isEmpty={isEmpty}
            columns={CUSTOM_SITE_TABLE_COLUMN_DEFS}
            sortedField={sortedField}
            isSortDescending={isSortDescending}
            onSort={onSort}
            sortButtonClassName="pre-enrollment-custom-table__sort-button"
        >
            {visibleRows.map((row) => {
                const actionLabel = getPreEnrollmentRowActionLabel(row.status);
                const isStarted = isPreEnrollmentCustomizationStarted(
                    row.status,
                );
                const actionClassName = isStarted
                    ? "pre-enrollment-custom-table__action-btn pre-enrollment-custom-table__action-btn--edit"
                    : "pre-enrollment-custom-table__action-btn pre-enrollment-custom-table__action-btn--start";

                return (
                    <tr key={row.id}>
                        <td>
                            {formatCustomSiteCellValue(row.clientName)}
                        </td>
                        <td>
                            {formatCustomSiteCellValue(row.oeUrlName)}
                        </td>
                        <td>
                            <span
                                className={`pre-enrollment-custom-table__status pre-enrollment-custom-table__status--${getPreEnrollmentStatusTone(
                                    row.status,
                                )}`}
                            >
                                {formatCustomSiteCellValue(row.status)}
                            </span>
                        </td>
                        <td>
                            <Link
                                to={`${PRE_ENROLLMENT_CUSTOM_ROUTE_PATHS.SITE_CUSTOMIZATION}/${encodeURIComponent(
                                    row.clientName,
                                )}`}
                                className={actionClassName}
                                title={actionLabel}
                                aria-label={actionLabel}
                                data-hover={actionLabel}
                            >
                                {isStarted ? (
                                    <LuPencil aria-hidden size={16} />
                                ) : (
                                    <LuPlay aria-hidden size={16} />
                                )}
                            </Link>
                        </td>
                    </tr>
                );
            })}
        </ListTableRegion>
    );
};

export default MainContent;
